// The single list of PDF & Pixel tools. It drives the home grid, the All/PDF/Image
// filter, "use this file in another tool" and each tool page's SEO text.
// Add a new tool here first.
import {
  IMAGE_KINDS,
  MAX_BATCH_FILES,
  kindOf,
  type FileKind,
} from "@/lib/files";

export type ToolCategory = "pdf" | "image";

export type Tool = {
  slug: string;
  name: string;
  /** One line shown on the tool card. */
  description: string;
  category: ToolCategory;
  accepts: FileKind[];
  /** How many files the tool takes at once (1 = single file). */
  maxFiles: number;
  /** Short verb for the action button, e.g. "Merge". */
  action: string;
  seoTitle: string;
  seoDescription: string;
  /** Three short steps for the "How to" section (NFR-10). */
  howTo: string[];
};

export const TOOLS: Tool[] = [
  {
    slug: "merge-pdf",
    name: "Merge PDF",
    description: "Join several PDFs into one, in any order.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 20,
    action: "Merge",
    seoTitle: "Merge PDF files free — no upload",
    seoDescription:
      "Combine up to 20 PDFs into one file and drag to reorder them. Free, no sign-up, and your files never leave your device.",
    howTo: [
      "Add two or more PDF files.",
      "Drag them into the order you want.",
      "Press Merge and download one combined PDF.",
    ],
  },
  {
    slug: "split-pdf",
    name: "Split PDF",
    description: "Pull out page ranges or split every page.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 1,
    action: "Split",
    seoTitle: "Split PDF by page range free — no upload",
    seoDescription:
      "Split a PDF by page ranges, every N pages, or one file per page. Free and private: it runs in your browser.",
    howTo: [
      "Add a PDF.",
      "Type page ranges like 1-3, 5, or choose every N pages or one file per page.",
      "Press Split and download the pages (several files come as a ZIP).",
    ],
  },
  {
    slug: "organize-pdf",
    name: "Organize PDF",
    description: "Reorder, rotate and delete pages.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 1,
    action: "Save",
    seoTitle: "Organize PDF pages — reorder, rotate, delete",
    seoDescription:
      "Drag PDF pages into a new order, rotate them or delete the ones you don't need. Free, no upload.",
    howTo: [
      "Add a PDF to see every page.",
      "Drag pages to reorder them, rotate or delete the ones you don't need.",
      "Press Save and download the new PDF.",
    ],
  },
  {
    slug: "edit-pdf",
    name: "Edit PDF",
    description: "Add text, images, shapes, highlights and white-out.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 1,
    action: "Save",
    seoTitle: "Edit PDF free — add text, images and highlights",
    seoDescription:
      "Add text, images, shapes, highlights and white-out to any PDF. Free, no sign-up, nothing is uploaded.",
    howTo: [
      "Add a PDF.",
      "Add text, images, shapes, highlights or white-out on any page.",
      "Press Save and download the edited PDF.",
    ],
  },
  {
    slug: "sign-pdf",
    name: "Sign PDF",
    description: "Draw, type or upload your signature.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 1,
    action: "Sign",
    seoTitle: "Sign PDF free — draw, type or upload a signature",
    seoDescription:
      "Sign a PDF with your finger, mouse, typed name or a photo of your signature. Private: it never leaves your device.",
    howTo: [
      "Add a PDF.",
      "Draw, type or upload your signature and place it on the page.",
      "Press Sign and download the signed PDF.",
    ],
  },
  {
    slug: "pdf-to-jpg",
    name: "PDF to JPG",
    description: "Turn PDF pages into JPG or PNG images.",
    category: "pdf",
    accepts: ["pdf"],
    maxFiles: 1,
    action: "Convert",
    seoTitle: "PDF to JPG or PNG free — no upload",
    seoDescription:
      "Save PDF pages as JPG or PNG images at 72, 150 or 300 DPI. Free and private.",
    howTo: [
      "Add a PDF.",
      "Choose JPG or PNG, the resolution and which pages.",
      "Press Convert and download the images (several come as a ZIP).",
    ],
  },
  {
    slug: "jpg-to-pdf",
    name: "Images to PDF",
    description: "Make one PDF from JPG, PNG, WebP or HEIC photos.",
    category: "pdf",
    accepts: ["jpg", "png", "webp", "heic"],
    maxFiles: MAX_BATCH_FILES,
    action: "Create PDF",
    seoTitle: "JPG to PDF — combine images into one PDF",
    seoDescription:
      "Turn photos and scans into one PDF with A4, Letter or fit-to-image pages. Free, no upload.",
    howTo: [
      "Add your photos or scans.",
      "Put them in order and pick page size, orientation and margins.",
      "Press Create PDF and download it.",
    ],
  },
  {
    slug: "resize-image",
    name: "Resize image",
    description: "Exact pixels, percent or cm, with exam-form presets.",
    category: "image",
    accepts: IMAGE_KINDS,
    maxFiles: MAX_BATCH_FILES,
    action: "Resize",
    seoTitle: "Resize image to exact pixels, cm or KB — free",
    seoDescription:
      "Resize photos by pixels, percent or cm at any DPI, with passport and exam-portal presets. Free, no upload.",
    howTo: [
      "Add one or more images (JPG, PNG, WebP, HEIC and more).",
      "Type a new size in pixels, percent or cm, or tap a quick size like Passport or Exam photo. Turn on “Keep under” to limit the file size in KB.",
      "Press Resize and download the image, or a ZIP for several images.",
    ],
  },
  {
    slug: "compress-image",
    name: "Compress image",
    description: "Shrink photos to a target size in KB.",
    category: "image",
    accepts: IMAGE_KINDS,
    maxFiles: MAX_BATCH_FILES,
    action: "Compress",
    seoTitle: "Compress image to 50 KB, 100 KB or any size — free",
    seoDescription:
      "Compress JPG, PNG and WebP photos with a quality slider or a target size in KB. Free and private.",
    howTo: [
      "Add one or more images.",
      "Move the quality slider or type a target size like 50 KB.",
      "Press Compress and download the smaller files.",
    ],
  },
  {
    slug: "convert-image",
    name: "Convert image",
    description: "HEIC, WebP, AVIF and more to JPG, PNG or WebP.",
    category: "image",
    accepts: IMAGE_KINDS,
    maxFiles: MAX_BATCH_FILES,
    action: "Convert",
    seoTitle: "Convert HEIC, WebP, PNG to JPG free — no upload",
    seoDescription:
      "Convert JPG, PNG, WebP, AVIF, GIF, HEIC and BMP images to JPG, PNG or WebP. Free, runs in your browser.",
    howTo: [
      "Add one or more images, including iPhone HEIC photos.",
      "Choose JPG, PNG or WebP, and a background colour for see-through parts.",
      "Press Convert and download the new files.",
    ],
  },
  {
    slug: "crop-image",
    name: "Crop image",
    description: "Crop to any ratio, rotate and flip.",
    category: "image",
    accepts: IMAGE_KINDS,
    maxFiles: 1,
    action: "Crop",
    seoTitle: "Crop image free — 1:1, 4:3, 16:9 or custom",
    seoDescription:
      "Crop photos to a free or fixed ratio, then rotate or flip them. Free, no sign-up, nothing uploaded.",
    howTo: [
      "Add an image.",
      "Drag the frame or pick a ratio like 1:1 or 16:9; rotate or flip if needed.",
      "Press Crop and download the image.",
    ],
  },
];

export type ToolFilter = "all" | ToolCategory;

export function getTool(slug: string): Tool | undefined {
  return TOOLS.find((t) => t.slug === slug);
}

export function filterTools(filter: ToolFilter, tools = TOOLS): Tool[] {
  return filter === "all" ? tools : tools.filter((t) => t.category === filter);
}

/** Tools that can open every one of these files (home suggestions,
 *  "use this file in another tool"). */
export function toolsForFiles(
  files: { name: string; type: string }[],
  tools = TOOLS,
): Tool[] {
  if (files.length === 0) return [];
  const kinds = files.map(kindOf);
  return tools.filter(
    (t) =>
      files.length <= t.maxFiles &&
      kinds.every((k) => k !== null && t.accepts.includes(k)),
  );
}
